import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DateComponent, DateVariant } from "components/base/DateComponent";
import React from "react";
import MinifiedTransactionQuotaBar from "./MinifiedTransactionQuotaBar";
import { ServiceProductPeriod } from "./types";

interface PeriodsWithQuotaProps {
  periods: ServiceProductPeriod[];
  transactionLimit: number;
  status?: string;
  currentPeriodNumber?: number | null;
}

const useStyles = makeStyles({
  root: {
    width: 634,
    border: "1px solid , #00000026",
    borderRadius: 3,
    marginLeft: "-16px"
  },
  label: {
    fontFamily: "Segoe UI",
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: 0,
    color: "#1E1E1E",
    marginBottom: 8
  },
  headerCell: {
    fontFamily: "Segoe UI",
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: 0,
    color: "#1E1E1E",
    borderTop: "1px solid #E0E0E0",
    borderBottom: "1px solid #E0E0E0",
    borderLeft: "1px solid #00000026",
    borderRight: "1px solid #00000026",
    padding: "0 8px",
    textAlign: "left",
    backgroundColor: "#E6E6E6",
    height: 24
  },
  periodCell: {
    width: 80,
    minWidth: 80,
    maxWidth: 80,
    padding: "0 8px"
  },
  startDateCell: {
    width: 136,
    minWidth: 136,
    maxWidth: 136,
    padding: "0 8px"
  },
  endDateCell: {
    width: 136,
    minWidth: 136,
    maxWidth: 136,
    padding: "0 8px"
  },
  usageHeaderCell: {
    fontFamily: "Segoe UI",
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: 0,
    color: "#1E1E1E",
    borderTop: "1px solid #E0E0E0",
    borderBottom: "1px solid #E0E0E0",
    borderLeft: "1px solid #00000026",
    borderRight: "1px solid #00000026",
    padding: "0 8px",
    textAlign: "left",
    backgroundColor: "#E6E6E6",
    height: 24,
    width: 160
  },
  usedTransactionsCell: {
    width: 114,
    minWidth: 114,
    maxWidth: 114,
    padding: "0 8px"
  },
  usageCell: {
    width: 160,
    minWidth: 160,
    maxWidth: 160,
    padding: "0 8px"
  },
  bodyCell: {
    fontFamily: "Segoe UI",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: 0,
    color: "#464646",
    borderLeft: "1px solid #00000026",
    borderRight: "1px solid #00000026",
    padding: 8
  },
  activeRow: {
    backgroundColor: "#C8F5FF"
  },
  usedTransactionsText: {
    fontFamily: "Segoe UI",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: 0,
    color: "#464646"
  },
  usedTransactionsExceeded: {
    color: "#DC6914"
  },
  usedTransactionsAllUsed: {
    color: "#DC0000"
  },
  barContainerActive: {
    background: "#ffffff"
  }
});

const PeriodsWithQuota: React.FC<PeriodsWithQuotaProps> = ({
  periods,
  transactionLimit,
  status,
  currentPeriodNumber
}) => {
  const classes = useStyles();

  function getUsedTransactionsClass(
    used: number,
    limit: number,
    classes: Record<string, string>,
    isExpired: boolean
  ) {
    if (isExpired) return classes.usedTransactionsText;
    if (used === limit) return classes.usedTransactionsAllUsed;
    if (used > limit) return classes.usedTransactionsExceeded;
    if (used > limit * 0.9) return classes.usedTransactionsExceeded;
    return classes.usedTransactionsText;
  }

  return (
    <Box className={classes.root}>
      <TableContainer sx={{ width: "100%", boxShadow: "none" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                className={`${classes.headerCell} ${classes.periodCell}`}
              >
                Period
              </TableCell>
              <TableCell
                className={`${classes.headerCell} ${classes.startDateCell}`}
              >
                Start date
              </TableCell>
              <TableCell
                className={`${classes.headerCell} ${classes.endDateCell}`}
              >
                End date
              </TableCell>
              <TableCell className={classes.usageHeaderCell}>Usage</TableCell>
              <TableCell
                className={`${classes.usageHeaderCell} ${classes.usedTransactionsCell}`}
              >
                Used transactions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {periods.map((period, idx) => {
              const isCurrent = currentPeriodNumber === idx + 1;
              const isActive = status === "Active";
              return (
                <TableRow
                  key={idx}
                  sx={{ border: 0 }}
                  className={
                    isCurrent && isActive ? classes.activeRow : undefined
                  }
                >
                  <TableCell
                    className={`${classes.bodyCell} ${classes.periodCell}`}
                  >{`${idx + 1}/${periods.length}`}</TableCell>
                  <TableCell
                    className={`${classes.bodyCell} ${classes.startDateCell}`}
                  >
                    <DateComponent
                      date={period.startDate}
                      variant={DateVariant.SHORT_WITH_TIME}
                    />
                  </TableCell>
                  <TableCell
                    className={`${classes.bodyCell} ${classes.endDateCell}`}
                  >
                    <DateComponent
                      date={period.endDate}
                      variant={DateVariant.SHORT_WITH_TIME}
                    />
                  </TableCell>
                  <TableCell
                    className={`${classes.bodyCell} ${classes.usageCell}`}
                  >
                    <MinifiedTransactionQuotaBar
                      usedTransactions={period.usedTransactionCount}
                      transactionLimit={transactionLimit}
                      barContainerClass={
                        isCurrent && isActive
                          ? classes.barContainerActive
                          : undefined
                      }
                      status={status}
                    />
                  </TableCell>
                  <TableCell
                    className={`${classes.bodyCell} ${classes.usedTransactionsCell}`}
                  >
                    <span
                      className={getUsedTransactionsClass(
                        period.usedTransactionCount,
                        transactionLimit,
                        classes,
                        status === "Expired"
                      )}
                    >
                      {period.usedTransactionCount}/{transactionLimit}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PeriodsWithQuota;
